/**
 * DingNest product page
 * Vanilla JS only. Enhances the DingNest sections without touching Horizon's
 * own components: the native product form, cart drawer and dynamic checkout
 * keep handling the actual commerce behaviour.
 *
 * Loaded once, as a module, from sections/dingnest-product-main.liquid. A module
 * is evaluated once per URL, so even if another section requested it the code
 * below would still run a single time.
 */
(function () {
  'use strict';

  if (window.DingNestProductPage) return;
  window.DingNestProductPage = { version: '1.0.0' };

  /* ----------------------------------------------------------------------
     Helpers
     ---------------------------------------------------------------------- */

  /**
   * Query all matches inside a scope, including the scope itself.
   * @param {string} selector
   * @param {Document|Element} scope
   * @returns {Element[]}
   */
  function collect(selector, scope) {
    var root = scope || document;
    var found = [];

    try {
      if (root.nodeType === 1 && root.matches && root.matches(selector)) {
        found.push(root);
      }
      Array.prototype.push.apply(found, root.querySelectorAll(selector));
    } catch (error) {
      return [];
    }

    return found;
  }

  /**
   * @param {Element} el
   * @param {string} flag
   * @returns {boolean} true when the element was not initialised before.
   */
  function claim(el, flag) {
    if (!el || el.dataset[flag] === 'true') return false;
    el.dataset[flag] = 'true';
    return true;
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Mueve un carril a una posicion concreta.
   *
   * El desplazamiento suave del navegador no siempre corre: en una pestana de
   * fondo la animacion no avanza y el carril se queda donde estaba. Medido en
   * la tienda: scrollTo con behavior 'smooth' dejaba scrollTop en 330 cuando el
   * destino era 0; con asignacion directa llegaba a 0 siempre.
   *
   * Asi que pedimos el suave y, pasado el tiempo de la animacion, comprobamos.
   * Si no llego, lo colocamos a mano. El cliente siempre acaba viendo la
   * miniatura activa, se anime o no.
   *
   * @param {Element} rail
   * @param {boolean} vertical
   * @param {number} to
   */
  function scrollRail(rail, vertical, to) {
    var axis = vertical ? 'scrollTop' : 'scrollLeft';

    if (prefersReducedMotion() || typeof rail.scrollTo !== 'function') {
      rail[axis] = to;
      return;
    }

    var how = { behavior: 'smooth' };
    if (vertical) how.top = to;
    else how.left = to;

    try {
      rail.scrollTo(how);
    } catch (error) {
      rail[axis] = to;
      return;
    }

    window.setTimeout(function () {
      if (Math.abs(rail[axis] - to) > 2) rail[axis] = to;
    }, 420);
  }

  /* ----------------------------------------------------------------------
     Gallery
     ---------------------------------------------------------------------- */

  /**
   * Activate a media item by id inside a gallery.
   * @param {Element} gallery
   * @param {string} mediaId
   * @param {boolean} [scrollIntoView]
   */
  function activateMedia(gallery, mediaId, scrollIntoView) {
    if (!gallery || !mediaId) return;

    var slides = gallery.querySelectorAll('[data-dn-slide]');
    var matched = null;

    for (var i = 0; i < slides.length; i++) {
      var isMatch = slides[i].getAttribute('data-media-id') === String(mediaId);
      slides[i].classList.toggle('is-active', isMatch);
      if (isMatch) matched = slides[i];
    }

    if (!matched) return;

    var thumbs = gallery.querySelectorAll('[data-dn-thumb]');
    var activeThumb = null;
    for (var t = 0; t < thumbs.length; t++) {
      var thumbMatch = thumbs[t].getAttribute('data-media-id') === String(mediaId);
      thumbs[t].classList.toggle('is-active', thumbMatch);
      thumbs[t].setAttribute('aria-pressed', thumbMatch ? 'true' : 'false');
      if (thumbMatch) activeThumb = thumbs[t];
    }

    // El carril de miniaturas se desplaza. Si la activa queda fuera, el cliente
    // ve un marco resaltado que no puede encontrar: la traemos a la vista.
    var rail = gallery.querySelector('[data-dn-thumbs]');
    if (activeThumb && rail) {
      var railScrolls = rail.scrollHeight > rail.clientHeight + 1 || rail.scrollWidth > rail.clientWidth + 1;
      if (railScrolls) {
        var vertical = rail.scrollHeight > rail.clientHeight + 1;
        var start = vertical ? activeThumb.offsetTop - rail.offsetTop : activeThumb.offsetLeft - rail.offsetLeft;
        var size = vertical ? activeThumb.offsetHeight : activeThumb.offsetWidth;
        var view = vertical ? rail.clientHeight : rail.clientWidth;
        var at = vertical ? rail.scrollTop : rail.scrollLeft;
        var target = at;

        if (start < at + 8) target = Math.max(0, start - 8);
        else if (start + size > at + view - 8) target = start + size - view + 8;

        if (target !== at) {
          scrollRail(rail, vertical, target);
        }
      }
    }

    var dots = gallery.querySelectorAll('[data-dn-dot]');
    for (var d = 0; d < dots.length; d++) {
      dots[d].classList.toggle('is-active', dots[d].getAttribute('data-media-id') === String(mediaId));
    }

    if (scrollIntoView) {
      var viewport = gallery.querySelector('[data-dn-gallery-viewport]');
      if (viewport && viewport.scrollWidth > viewport.clientWidth) {
        scrollRail(viewport, false, matched.offsetLeft - viewport.offsetLeft);
      }
    }
  }

  /**
   * @param {Element} gallery
   */
  function initGallery(gallery) {
    if (!claim(gallery, 'dnGalleryInit')) return;

    var viewport = gallery.querySelector('[data-dn-gallery-viewport]');
    var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-dn-thumb]'));

    thumbs.forEach(function (thumb, index) {
      thumb.addEventListener('click', function () {
        activateMedia(gallery, thumb.getAttribute('data-media-id'), true);
      });

      thumb.addEventListener('keydown', function (event) {
        var offset = 0;
        if (event.key === 'ArrowRight') offset = 1;
        else if (event.key === 'ArrowLeft') offset = -1;
        else if (event.key === 'Home') offset = -index;
        else if (event.key === 'End') offset = thumbs.length - 1 - index;
        else return;

        event.preventDefault();
        var next = thumbs[Math.max(0, Math.min(thumbs.length - 1, index + offset))];
        if (!next) return;
        next.focus();
        activateMedia(gallery, next.getAttribute('data-media-id'), true);
      });
    });

    // Ya no hay baldosa "+N": cada foto tiene su miniatura y el carril rueda.

    // Al cargar, la foto activa es la destacada de la variante, que no tiene
    // por que ser la primera. En movil el carril arranca igualmente en la
    // primera, asi que el punto y el zoom apuntaban a una foto distinta de la
    // que se ve. Alineamos todo con la activa antes de escuchar nada.
    var current = gallery.querySelector('[data-dn-slide].is-active');
    if (current) activateMedia(gallery, current.getAttribute('data-media-id'), true);

    // Keep dots in sync while swiping on mobile.
    if (viewport) {
      var scrollTimer = null;
      viewport.addEventListener(
        'scroll',
        function () {
          if (scrollTimer) window.clearTimeout(scrollTimer);
          scrollTimer = window.setTimeout(function () {
            if (viewport.scrollWidth <= viewport.clientWidth) return;
            var slides = Array.prototype.slice.call(gallery.querySelectorAll('[data-dn-slide]'));
            if (!slides.length) return;

            var center = viewport.scrollLeft + viewport.clientWidth / 2;
            var closest = slides[0];
            var closestDistance = Infinity;

            slides.forEach(function (slide) {
              var slideCenter = slide.offsetLeft - viewport.offsetLeft + slide.offsetWidth / 2;
              var distance = Math.abs(slideCenter - center);
              if (distance < closestDistance) {
                closestDistance = distance;
                closest = slide;
              }
            });

            var id = closest.getAttribute('data-media-id');
            if (id) activateMedia(gallery, id, false);
          }, 90);
        },
        { passive: true }
      );
    }
  }

  /* ----------------------------------------------------------------------
     Zoom dialog
     ---------------------------------------------------------------------- */

  function initZoom(section) {
    var dialog = section.querySelector('[data-dn-zoom-dialog]');
    var trigger = section.querySelector('[data-dn-zoom-open]');
    if (!dialog || !trigger) return;
    if (!claim(dialog, 'dnZoomInit')) return;
    if (typeof dialog.showModal !== 'function') {
      trigger.hidden = true;
      return;
    }

    var body = dialog.querySelector('[data-dn-zoom-body]');
    var closeButton = dialog.querySelector('[data-dn-zoom-close]');
    var image = null;

    trigger.addEventListener('click', function () {
      var active = section.querySelector('[data-dn-slide].is-active');
      var src = active && active.getAttribute('data-zoom-src');

      if (!src) {
        var fallback = active && active.querySelector('img');
        src = fallback ? fallback.currentSrc || fallback.src : '';
      }

      if (!src || !body) return;

      if (!image) {
        image = document.createElement('img');
        image.setAttribute('loading', 'eager');
        image.setAttribute('decoding', 'async');
        body.appendChild(image);
      }

      image.setAttribute('src', src);
      image.setAttribute('alt', (active && active.getAttribute('data-zoom-alt')) || '');

      try {
        dialog.showModal();
      } catch (error) {
        /* no-op */
      }
    });

    if (closeButton) {
      closeButton.addEventListener('click', function () {
        dialog.close();
      });
    }

    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) dialog.close();
    });

    dialog.addEventListener('close', function () {
      if (image && image.parentNode) image.parentNode.removeChild(image);
      image = null;
      trigger.focus();
    });
  }

  /* ----------------------------------------------------------------------
     Quantity
     ---------------------------------------------------------------------- */

  function initQuantity(section) {
    collect('[data-dn-quantity]', section).forEach(function (wrapper) {
      if (!claim(wrapper, 'dnQtyInit')) return;

      var input = wrapper.querySelector('[data-dn-qty-input]');
      if (!input) return;

      wrapper.querySelectorAll('[data-dn-qty-step]').forEach(function (button) {
        button.addEventListener('click', function () {
          var step = parseInt(input.getAttribute('step'), 10) || 1;
          var direction = parseInt(button.getAttribute('data-dn-qty-step'), 10) || 1;
          var min = parseInt(input.getAttribute('min'), 10) || 1;
          var maxAttr = input.getAttribute('max');
          var max = maxAttr ? parseInt(maxAttr, 10) : Infinity;
          var current = parseInt(input.value, 10);

          if (isNaN(current)) current = min;

          var next = Math.min(max, Math.max(min, current + step * direction));
          if (next === current) return;

          input.value = String(next);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      input.addEventListener('change', function () {
        var min = parseInt(input.getAttribute('min'), 10) || 1;
        var maxAttr = input.getAttribute('max');
        var max = maxAttr ? parseInt(maxAttr, 10) : Infinity;
        var value = parseInt(input.value, 10);

        if (isNaN(value)) value = min;
        input.value = String(Math.min(max, Math.max(min, value)));
      });
    });
  }

  /* ----------------------------------------------------------------------
     Wishlist placeholder
     ---------------------------------------------------------------------- */

  function initWishlist(section) {
    collect('[data-dn-wishlist]', section).forEach(function (button) {
      if (!claim(button, 'dnWishlistInit')) return;

      button.addEventListener('click', function () {
        var pressed = button.getAttribute('aria-pressed') === 'true';
        button.setAttribute('aria-pressed', pressed ? 'false' : 'true');

        button.dispatchEvent(
          new CustomEvent('dingnest:wishlist:toggle', {
            bubbles: true,
            detail: {
              productId: button.getAttribute('data-product-id'),
              variantId: button.getAttribute('data-variant-id'),
              handle: button.getAttribute('data-product-handle'),
              active: !pressed,
            },
          })
        );
      });
    });
  }

  /* ----------------------------------------------------------------------
     Variants
     ---------------------------------------------------------------------- */

  function initVariants(section) {
    if (!claim(section, 'dnVariantsInit')) return;

    var dataEl = section.querySelector('[data-dn-variant-data]');
    if (!dataEl) return;

    var payload;
    try {
      payload = JSON.parse(dataEl.textContent || '{}');
    } catch (error) {
      return;
    }

    var variants = (payload && payload.variants) || [];
    if (!variants.length) return;

    var gallery = section.querySelector('[data-dn-gallery]');
    var idInput = section.querySelector('[data-dn-variant-id]');
    var priceCurrent = section.querySelector('[data-dn-price-current]');
    var priceCompare = section.querySelector('[data-dn-price-compare]');
    var priceUnit = section.querySelector('[data-dn-price-unit]');
    var atcButton = section.querySelector('[data-dn-atc-button]');
    var atcLabel = section.querySelector('[data-dn-atc-label]');
    var atcPrice = section.querySelector('[data-dn-atc-price]');
    var priceSave = section.querySelector('[data-dn-price-save]');
    var dynamicCheckout = section.querySelector('[data-dn-dynamic-checkout]');
    var wishlist = section.querySelector('[data-dn-wishlist]');
    var optionInputs = Array.prototype.slice.call(section.querySelectorAll('[data-dn-option-input]'));

    var labels = {
      add: section.getAttribute('data-label-add') || 'Add to Cart',
      soldOut: section.getAttribute('data-label-sold-out') || 'Sold out',
      unavailable: section.getAttribute('data-label-unavailable') || 'Unavailable',
    };

    function readSelection() {
      var selection = [];

      optionInputs.forEach(function (input) {
        var index = parseInt(input.getAttribute('data-option-index'), 10);
        if (isNaN(index)) return;

        if (input.tagName === 'SELECT') {
          selection[index] = input.value;
        } else if (input.checked) {
          selection[index] = input.value;
        }
      });

      return selection;
    }

    function matchVariant(selection) {
      for (var i = 0; i < variants.length; i++) {
        var variant = variants[i];
        var options = variant.options || [];
        var matches = true;

        for (var o = 0; o < selection.length; o++) {
          if (selection[o] === undefined) continue;
          if (String(options[o]) !== String(selection[o])) {
            matches = false;
            break;
          }
        }

        if (matches) return variant;
      }

      return null;
    }

    function markAvailability(selection) {
      optionInputs.forEach(function (input) {
        if (input.tagName === 'SELECT') return;

        var index = parseInt(input.getAttribute('data-option-index'), 10);
        if (isNaN(index)) return;

        var probe = selection.slice();
        probe[index] = input.value;

        var available = variants.some(function (variant) {
          if (!variant.available) return false;
          var options = variant.options || [];

          for (var o = 0; o < probe.length; o++) {
            if (probe[o] === undefined) continue;
            if (String(options[o]) !== String(probe[o])) return false;
          }

          return true;
        });

        var label = input.id ? section.querySelector('label[for="' + input.id + '"]') : null;
        if (label) label.setAttribute('data-dn-unavailable', available ? 'false' : 'true');
      });
    }

    function updateLegends(selection) {
      section.querySelectorAll('[data-dn-option-group]').forEach(function (group) {
        var index = parseInt(group.getAttribute('data-option-index'), 10);
        var output = group.querySelector('[data-dn-option-selected]');
        if (!output || isNaN(index)) return;
        output.textContent = selection[index] || '';
      });
    }

    function updateUrl(variant) {
      if (!variant || !window.history || !window.history.replaceState) return;
      if (window.Shopify && window.Shopify.designMode) return;
      if (!payload.productUrl) return;

      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      } catch (error) {
        /* no-op */
      }
    }

    function update() {
      var selection = readSelection();
      var variant = matchVariant(selection);

      updateLegends(selection);
      markAvailability(selection);

      if (idInput) {
        idInput.value = variant ? String(variant.id) : '';
        idInput.disabled = !(variant && variant.available);
      }

      if (variant && atcPrice) {
        atcPrice.textContent = variant.price || '';
      }

      // El badge de ahorro solo tiene sentido si la variante esta rebajada.
      if (variant && priceSave) {
        var was = parseFloat(String(variant.compareAtPrice || '').replace(/[^0-9.,]/g, '').replace(',', '.'));
        var now = parseFloat(String(variant.price || '').replace(/[^0-9.,]/g, '').replace(',', '.'));
        var off = was && now && was > now ? Math.round(((was - now) / was) * 100) : 0;
        priceSave.classList.toggle('dn-hidden', off <= 0);
        if (off > 0) {
          priceSave.textContent = (section.getAttribute('data-label-save') || 'Save') + ' ' + off + '%';
        }
      }

      if (variant && priceCurrent) {
        priceCurrent.textContent = variant.price || '';
        priceCurrent.classList.toggle('dn-price__current--sale', Boolean(variant.compareAtPrice));
      }

      if (priceCompare) {
        if (variant && variant.compareAtPrice) {
          priceCompare.textContent = variant.compareAtPrice;
          priceCompare.classList.remove('dn-hidden');
        } else {
          priceCompare.textContent = '';
          priceCompare.classList.add('dn-hidden');
        }
      }

      if (priceUnit) {
        if (variant && variant.unitPrice) {
          priceUnit.textContent = variant.unitPrice;
          priceUnit.classList.remove('dn-hidden');
        } else {
          priceUnit.textContent = '';
          priceUnit.classList.add('dn-hidden');
        }
      }

      if (atcButton) {
        var canAdd = Boolean(variant && variant.available);
        atcButton.disabled = !canAdd;
        if (atcLabel) {
          atcLabel.textContent = canAdd ? labels.add : variant ? labels.soldOut : labels.unavailable;
        }
      }

      if (dynamicCheckout) {
        dynamicCheckout.hidden = !(variant && variant.available);
      }

      if (wishlist && variant) {
        wishlist.setAttribute('data-variant-id', String(variant.id));
      }

      if (variant && variant.featuredMediaId && gallery) {
        activateMedia(gallery, variant.featuredMediaId, true);
      }

      updateUrl(variant);
    }

    optionInputs.forEach(function (input) {
      input.addEventListener('change', update);
    });

    // Sync once so any pre-selected state is reflected without changing the URL.
    markAvailability(readSelection());
  }

  /* ----------------------------------------------------------------------
     Boot

     Note: the product recommendations fetch deliberately lives in
     sections/dingnest-product-recommendations.liquid instead of here, so that
     section stays self-contained and contributes no extra script tag.
     ---------------------------------------------------------------------- */

  function init(scope) {
    var root = scope && scope.nodeType === 1 ? scope : document;

    collect('[data-dn-gallery]', root).forEach(initGallery);
    collect('[data-dn-product-main]', root).forEach(function (section) {
      initZoom(section);
      initQuantity(section);
      initWishlist(section);
      initVariants(section);
      initSizeGuide(section);
    });
  }


  // Guia de tallas. Un dialogo nativo: cierra con Escape y con el fondo sin JS extra.
  function initSizeGuide(root) {
    var dialogs = root.querySelectorAll('[data-dn-size-guide]');

    Array.prototype.forEach.call(dialogs, function (dialog) {
      if (dialog.dataset.dnGuideInit === 'true') return;
      dialog.dataset.dnGuideInit = 'true';

      var section = dialog.closest('[data-dn-product-main]') || document;
      var openers = section.querySelectorAll('[data-dn-size-guide-open]');
      var closer = dialog.querySelector('[data-dn-size-guide-close]');

      Array.prototype.forEach.call(openers, function (b) {
        b.addEventListener('click', function () {
          if (typeof dialog.showModal === 'function') dialog.showModal();
          else dialog.setAttribute('open', '');
        });
      });

      if (closer) {
        closer.addEventListener('click', function () {
          if (typeof dialog.close === 'function') dialog.close();
          else dialog.removeAttribute('open');
        });
      }

      // Clic en el fondo, fuera del contenido.
      dialog.addEventListener('click', function (event) {
        if (event.target === dialog && typeof dialog.close === 'function') dialog.close();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:select', function (event) {
    init(event.target);
  });

  window.DingNestProductPage.init = init;
  window.DingNestProductPage.activateMedia = activateMedia;
})();
